import { ExpandMore, KeyboardArrowDown } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Box, Collapse, IconButton, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import {useFormContext, useWatch} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";
import { useProductQuery, useTotalToBePaidQueryObserver } from "../../../../api/services";
import { accordionExpandedState, isBusinessContinuityMode } from "../../recoil";
import { IFormValues } from "../../types";
import { setDecimalSeparator } from "../../../../utils";

const AMOUNT_WIDTH = 200;

const TotalToBePaid = () => {

    
    const { t, i18n } = useTranslation('booking');
    const formContext = useFormContext<IFormValues>();

    const businessContinuityMode = useRecoilValue(isBusinessContinuityMode);
    const [totalExpandend, setTotalExpanded] = useRecoilState(accordionExpandedState('total'));

    const [ancillariesExpanded, setAncillariesExpanded] = useState<boolean>(false);
    const handleAncillariesToggle = useCallback(() => {
        setAncillariesExpanded(c => !c);
    }, [setAncillariesExpanded]);

    const rentalType = useWatch<IFormValues, 'basicDetails.rentalType'>({
        name: 'basicDetails.rentalType'
    });
    const tenant = useWatch({ control: formContext.control, name: "basicDetails.tenants" });
    const dealer = useWatch({ control: formContext.control, name: "basicDetails.dealers" });

    const productQuery = useProductQuery({
        segment: rentalType,
        businessContinuityInsToolMode: businessContinuityMode,
        tenant: tenant as string,
        dealer: dealer as string
    }, {
        refetchOnMount: false
    });
    const totalToBePaidQuery = useTotalToBePaidQueryObserver({tenant: tenant as string, dealer: dealer as string});

    return (
        <Accordion expanded={totalExpandend} onChange={(e, expanded) => setTotalExpanded(expanded)}>
            <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h5">
                    {t('label.priceSummary')}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Box sx={{ overflowX: 'auto' }}>
                    <Stack direction="row" mb={1}>
                        <Typography
                            variant="h6"
                            flex={1}
                        >
                            {t('label.summary')}
                        </Typography>
                        {productQuery?.data?.products?.map(p => (
                            <Typography
                                key={p.code}
                                variant="h6"
                                width={AMOUNT_WIDTH}
                                textAlign="end"
                            >
                                {p.label}
                            </Typography>
                        ))}
                    </Stack>
                    {totalToBePaidQuery.data &&
                        <>
                            <Stack direction="row" mb={.75}>
                                <Typography
                                    variant="bodySmall"
                                    flex={1}
                                >
                                    {t('label.rentalAmount')}
                                </Typography>
                                {productQuery?.data?.products?.map((p: any) => (
                                    <Typography
                                        key={p.code}
                                        variant="bodySmall"
                                        width={AMOUNT_WIDTH}
                                        textAlign="end"
                                    >
                                        {(totalToBePaidQuery.data?.rate[p.code]?.[0].rate ?? 0) !== (totalToBePaidQuery.data?.rate[p.code]?.[1].rate ?? 0) &&
                                            <Typography
                                                variant="caption"
                                            >
                                                {'('}
                                                <span
                                                    style={{
                                                        textDecoration: 'line-through'
                                                    }}
                                                >
                                                    { totalToBePaidQuery.data?.rate[p.code] ? (
                                                            setDecimalSeparator(((totalToBePaidQuery.data?.rate[p.code]?.[0].rate ?? 0) / 100).toFixed(2), i18n.language) +
                                                            ' ' +
                                                            totalToBePaidQuery.data?.rate[p.code]?.[0].currency
                                                        ) : ' - '
                                                    }
                                                </span>
                                                {') '}
                                            </Typography>
                                        }
                                        { totalToBePaidQuery.data?.rate[p.code] ? (
                                                setDecimalSeparator(((totalToBePaidQuery.data?.rate[p.code]?.[1].rate ?? 0) / 100).toFixed(2), i18n.language) +
                                                ' ' +
                                                totalToBePaidQuery.data?.rate[p.code]?.[1].currency
                                            ) : ' - '
                                        }
                                    </Typography>
                                ))}
                            </Stack>
                            {totalToBePaidQuery.data?.ancillariesTotal &&
                                <>
                                    <Stack direction="row">
                                        <Typography
                                            variant="bodySmall"
                                            flex={1}
                                        >
                                            {t('label.ancillaries')}
                                            <IconButton
                                                sx={{ p: 0, ml: 1 }}
                                                onClick={handleAncillariesToggle}
                                            >
                                                <KeyboardArrowDown fontSize="inherit" />
                                            </IconButton>
                                        </Typography>
                                        {productQuery.data?.products?.map((p) => (
                                            <Typography
                                                key={p.code}
                                                variant="bodySmall"
                                                width={AMOUNT_WIDTH}
                                                textAlign="end"
                                            >
                                                {(totalToBePaidQuery.data?.ancillariesTotal?.amount[p.code]?.[0] ?? 0) !== (totalToBePaidQuery.data?.ancillariesTotal?.amount[p.code]?.[1] ?? 0) &&
                                                    <Typography
                                                        variant="caption"
                                                    >
                                                        {'('}
                                                        <span
                                                            style={{
                                                                textDecoration: 'line-through'
                                                            }}
                                                        >
                                                            { totalToBePaidQuery.data?.ancillariesTotal?.amount[p.code]  ? (
                                                                    setDecimalSeparator(((totalToBePaidQuery.data?.ancillariesTotal?.amount[p.code]?.[0] ?? 0) / 100).toFixed(2), i18n.language) +
                                                                    ' ' +
                                                                    totalToBePaidQuery.data?.ancillariesTotal?.currency
                                                                ) : ' - '
                                                            }
                                                        </span>
                                                        {') '}
                                                    </Typography>
                                                }
                                                { totalToBePaidQuery.data?.ancillariesTotal?.amount[p.code] ? (
                                                        setDecimalSeparator(((totalToBePaidQuery.data?.ancillariesTotal?.amount[p.code]?.[1] ?? 0) / 100).toFixed(2), i18n.language) +
                                                        ' ' +
                                                        totalToBePaidQuery.data?.ancillariesTotal?.currency
                                                    ) : ' - '
                                                }
                                            </Typography>
                                        ))}
                                    </Stack>
                                    <Collapse
                                        in={ancillariesExpanded}
                                        timeout="auto"
                                        sx={{ mb: .75 }}
                                    >
                                        {totalToBePaidQuery.data?.ancillariesTotal.list.map(a => (
                                            <Stack key={a.code} direction="row">
                                                <Typography
                                                    variant="bodySmall"
                                                    flex={1}
                                                    pl={2}
                                                    boxSizing="border-box"
                                                >
                                                    {a.label}
                                                </Typography>
                                                {productQuery.data?.products?.map((p) => (
                                                    <Typography
                                                        key={p.code}
                                                        variant="bodySmall"
                                                        width={AMOUNT_WIDTH}
                                                        textAlign="end"
                                                    >
                                                        {(a.amount[p.code]?.[0] ?? 0) !== (a.amount[p.code]?.[1] ?? 0) &&
                                                            <Typography
                                                                variant="caption"
                                                            >
                                                                {'('}
                                                                <span
                                                                    style={{
                                                                        textDecoration: 'line-through'
                                                                    }}
                                                                >
                                                                    { a.amount[p.code] ? (
                                                                            setDecimalSeparator(((a.amount[p.code]?.[0] ?? 0) / 100).toFixed(2), i18n.language) +
                                                                            ' ' +
                                                                            a.currency
                                                                        ) : ' - '
                                                                    }
                                                                </span>
                                                                {') '}
                                                            </Typography>
                                                        }
                                                        { a.amount[p.code] ? (
                                                                setDecimalSeparator(((a.amount[p.code]?.[1] ?? 0) / 100).toFixed(2), i18n.language) +
                                                                ' ' +
                                                                a.currency
                                                            ) : ' - '
                                                        }
                                                    </Typography>
                                                ))}
                                            </Stack>
                                        ))}
                                    </Collapse>
                                </>
                            }
                            {totalToBePaidQuery.data?.promo &&
                                <Stack direction="row" mb={.75}>
                                    <Typography
                                        variant="bodySmall"
                                        flex={1}
                                    >
                                        {t('label.appliedDiscount')}
                                    </Typography>
                                    {productQuery.data?.products?.map((p) => (
                                        <Typography
                                            key={p.code}
                                            variant="bodySmall"
                                            width={AMOUNT_WIDTH}
                                            textAlign="end"
                                        >
                                            {`${setDecimalSeparator(((totalToBePaidQuery.data?.promo?.[p.code] ?? 0) / 100 * -1).toFixed(2), i18n.language)} €`}
                                        </Typography>
                                    ))}
                                </Stack>
                            }

                            <Stack direction="row" mb={2.5}>
                                <Typography
                                    variant="h5"
                                    flex={1}
                                >
                                    {t('label.totalToBePaid')}
                                </Typography>
                                {productQuery.data?.products?.map((p) => (
                                    <Typography
                                        key={p.code}
                                        variant="h5"
                                        width={AMOUNT_WIDTH}
                                        textAlign="end"
                                    >
                                        {(totalToBePaidQuery.data?.totalToBePaid[p.code]?.[0].total ?? 0) !== (totalToBePaidQuery.data?.totalToBePaid[p.code]?.[1].total ?? 0) &&
                                            <Typography
                                                variant="caption"
                                            >
                                                {'('}
                                                <span
                                                    style={{
                                                        textDecoration: 'line-through'
                                                    }}
                                                >
                                                    { totalToBePaidQuery.data?.totalToBePaid[p.code] ? (
                                                            setDecimalSeparator(((totalToBePaidQuery.data?.totalToBePaid[p.code]?.[0].total ?? 0) / 100).toFixed(2), i18n.language) +
                                                            ' ' +
                                                            totalToBePaidQuery.data?.totalToBePaid[p.code]?.[0].currency
                                                        ) : ' - '
                                                    }
                                                </span>
                                                {') '}
                                            </Typography>
                                        }
                                        { totalToBePaidQuery.data?.totalToBePaid[p.code] ? (
                                                setDecimalSeparator(((totalToBePaidQuery.data?.totalToBePaid[p.code]?.[1].total ?? 0) / 100).toFixed(2), i18n.language) +
                                                ' ' +
                                                totalToBePaidQuery.data?.totalToBePaid[p.code]?.[1].currency
                                            ) : ' - '
                                        }
                                    </Typography>
                                ))}
                            </Stack>

                            <Stack direction="row" mb={.75}>
                                <Typography
                                    variant="bodySmall"
                                    flex={1}
                                >
                                    {t('label.totalTaxExcluded')}
                                </Typography>
                                {productQuery.data?.products?.map((p) => (
                                    <Typography
                                        key={p.code}
                                        variant="bodySmall"
                                        width={AMOUNT_WIDTH}
                                        textAlign="end"
                                    >
                                        { totalToBePaidQuery.data?.totalToBePaidTaxExcluded[p.code] ? (
                                                setDecimalSeparator(((totalToBePaidQuery.data?.totalToBePaidTaxExcluded[p.code]?.total ?? 0) / 100).toFixed(2), i18n.language) +
                                                ' ' +
                                                totalToBePaidQuery.data?.totalToBePaidTaxExcluded[p.code]?.currency
                                            ) : ' - '
                                        }
                                    </Typography>
                                ))}
                            </Stack>

                            {totalToBePaidQuery.data?.excessesTotal.map((v) => (
                                <Stack key={v.code} direction="row" mb={.75}>
                                    <Typography
                                        variant="bodySmall"
                                        flex={1}
                                    >
                                        {t(`label.excesses.${v.code}`, { defaultValue: v.label })}
                                    </Typography>
                                    {productQuery?.data?.products?.map((p: any) => (
                                        <Typography
                                            key={p.code}
                                            variant="bodySmall"
                                            width={AMOUNT_WIDTH}
                                            textAlign="end"
                                        >
                                            {(v.amount[p.code]?.[0] ?? 0) !== (v.amount[p.code]?.[1] ?? 0) &&
                                                <Typography
                                                    variant="caption"
                                                >
                                                    {'('}
                                                    <span
                                                        style={{
                                                            textDecoration: 'line-through'
                                                        }}
                                                    >
                                                        { v.amount[p.code] ?
                                                            `${setDecimalSeparator(((v.amount[p.code]?.[0] ?? 0) / 100).toFixed(2), i18n.language)} €` : ' - '
                                                        }
                                                    </span>
                                                    {')'}
                                                </Typography>
                                            }
                                            { v.amount[p.code] ? (
                                                    setDecimalSeparator(((v.amount[p.code]?.[1] ?? 0) / 100).toFixed(2), i18n.language) +
                                                    ' ' +
                                                    v.currency
                                                ) : ' - '
                                            }
                                        </Typography>
                                    ))}
                                </Stack>
                            ))}
                            <Stack direction="row">
                                <Typography
                                    variant="bodySmall"
                                    flex={1}
                                >
                                    {t('label.extraKmAmount')}
                                </Typography>
                                {productQuery?.data?.products?.map((p) => (
                                    <Typography
                                        key={p.code}
                                        variant="bodySmall"
                                        width={AMOUNT_WIDTH}
                                        textAlign="end"
                                    >
                                        { totalToBePaidQuery.data?.extraKmAmount[p.code] ? (
                                                setDecimalSeparator(((totalToBePaidQuery.data?.extraKmAmount[p.code]?.amount ?? 0) / 100).toFixed(2), i18n.language) +
                                                ' ' +
                                                totalToBePaidQuery.data?.extraKmAmount[p.code]?.currency
                                            ) : ' - '
                                        }
                                    </Typography>
                                ))}
                            </Stack>
                        </>
                    }
                </Box>
            </AccordionDetails>
        </Accordion >
    );
};

export default TotalToBePaid;
